/**
 * @module experience/shop/product-details
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
// CORE
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subject } from 'rxjs';

// INTERFACE
import { ProductDetail, Product, ItemDetail } from '@support/api/common/product.interface';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';

// SERVICE
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { ProductDetailsService } from '@experience/shop/services/product-details.service';

// MODEL
import { ProductDetailsDefaultModel } from '@experience/shop/shared-browse/models/productDetails-default.model';
import { APIError } from '@support/api/common/error.interface';
import { TileManagementService } from '@support/shared/services/tile-management.service';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import {
  HARDGOODS_CONSTANTS,
} from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { takeUntil } from 'rxjs/operators';
import { PdlWrapperService } from '@core/services/pdl-wrapper.service';
import { PdpDataHandlerService } from '@root/shared/services/pdp-data-handler.service';
import { ContainerData } from '@core/aem/components/container/models/container-data.interface';
import { ContentItem } from '@core/aem/models/content-item.interface';

@Component({
  selector: 'tmo-device-details-landing',
  templateUrl: './device-details-landing.component.html',
  styleUrls: [],
})
export class DeviceDetailsLandingComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  /** Variable to hold product id */
  public canonicalUrl: string;
  /** Variable to hold PDP title */
  public productDetailTitle: string;
  /** Variable to hold authorable content coming from AEM call */
  public pdpAuthorableData: Object;
  /** Variable to hold static content coming from AEM call */
  public pdpStaticContent: ConfigData;

  public showSpinner: boolean;
  public productDetails: ProductDetailsDefaultModel;
  /**
   * stores family name of the selected device
   */
  public productName: string;
  /**
   * checks if page is device landing
   */
  public deviceLanding: boolean;
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;

  /** Variable to hold productType of selected product */
  public productType: string;

  /**
   * Holds boolean to check if product is available or not
   */
  public isProductAvailable: boolean;
  /**
   * Flag to identify if is tourist simkit
   */
  public isTouristSimkit: boolean;

  public isOnlyDevice: boolean;

  constructor(
    public productDetailsService: ProductDetailsService,
    private route: ActivatedRoute,
    private stateUtilService: StateStoreUtilsService,
    private cartsupportService: CartSupportService,
    private commonUtilService: CommonUtilService,
    private objectService: ObjectService,
    private tileManagementService: TileManagementService,
    public pdlService: PdlService,
    public pdlWrapperService: PdlWrapperService,
    private pdpDataHandlerService: PdpDataHandlerService,
    private router: Router
  ) {
    super(pdlService, route);
    this.showSpinner = true;
    this.ngUnsubscribe = new Subject<void>();
    this.deviceLanding = true;
    this.productDetailTitle = '';
    // this.channel = SETTINGS.CHANNEL.toLowerCase();
  }

  /**
   * Component lifecycle hook to initialize component
   * @returns void
   */
  public ngOnInit(): void {
    const zipCode =
      this.objectService.get(this.stateUtilService.getBillingAddress(), 'postalCode') ||
      this.objectService.get(this.stateUtilService.getCustomerConfigData(), 'zip');
    if (zipCode) {
      this.initializeComponentAfterZipCodeAvailability();
    }
    this.productName =
      this.objectService.get(
        this.tileManagementService.getFocusedTileIdDetails(),
        'deviceDetails.familyName'
      ) || '';
    // if (this.channel.toLowerCase() === CHANNELS.WEB) {
    this.productDetailsService.publishMetaBeforeContent();
    // }
  }

  /**
   * Component lifecycle hook to destroy component
   * @returns void
   */
  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  /**
   * Function to initialize the component
   */
  public initializeComponentAfterZipCodeAvailability(): void {
    // this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'pageLoad');
    this.productDetailsService
      .getProductDetailsContent()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((content: ConfigData) => {
        this.pdpStaticContent = content;
        this.fetchOfferingIdAndLoadProductDetails();
      });
    this.cartsupportService.deviceAndSimToCart$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: boolean) => {
        this.showSpinner = data;
      });
  }
  /**
   * getting the canonicalUrl based on new line or edit line scenario
   * @returns void
   */

  private fetchOfferingIdAndLoadProductDetails(): void {
    this.route.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params: Params) => {
      this.canonicalUrl = params.device || params.canonicalUrl;
      this.isTouristSimkit = this.canonicalUrl === HARDGOODS_CONSTANTS.TOURIST_SIM_KIT;
      const selectedColor = params.color ? params.color : '';
      const isRefurbished = this.route.snapshot.queryParams.isRefurbished;
      this.stateUtilService.setSelectedColor(selectedColor);
      this.loadProductDetails(this.canonicalUrl, isRefurbished);
    });
  }

  /**
   * Loads data from stateStore or from API if product details exists
   * @param canonicalUrl canonicalUrl to identify product details
   * @param hasCpo boolean
   * @returns void
   */
  private loadProductDetails(canonicalUrl: string, hasCpo?: boolean): void {
    // this.loadProductDetailsInPDP(canonicalUrl);
    const cachedProductDetails = this.productDetailsService.getCachedProductDetails(canonicalUrl);
    if (this.commonUtilService.isNotEmpty(cachedProductDetails)) {
      this.productType = cachedProductDetails.productType;
    }
    const itemDetailsFromCache = this.objectService.get(
      this.stateUtilService.getProductDetails(canonicalUrl),
      'itemDetails'
    );
    const productType = this.productType
      ? this.productType
      : this.stateUtilService.getProductType();
    // @TODO alter true to false and false to true
    this.productDetailsService
      .getProductDetails(canonicalUrl, productType, hasCpo, itemDetailsFromCache ? 'true' : 'true')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (response: ProductDetail) => {
          if (this.objectService.get(response, 'model-view')) {
            const productData: Product = this.objectService.get(
              response,
              'model-view.model.model-data.product-offerings.productDetailsResponse'
            );
            const productDetails = productData || null;
            const productCanonicalUrl = this.objectService.get(productData, 'familyId');
            this.stateUtilService.setSelectedOfferId(productCanonicalUrl);
            const productfamilyName = this.objectService.get(productData, 'familyName');
            this.productName = productfamilyName;
            this.stateUtilService.setSelectedDeviceFamilyName(productfamilyName);
            this.setSelectionDetails(productDetails, productCanonicalUrl, itemDetailsFromCache);
            this.setJunkFeeBanner();
            this.pdpAuthorableData = this.objectService.get(
              response,
              'model-view.model.model-data.module-enhancements'
            );
            this.pdpAuthorableData[0].data.items[0].data.items =
              this.pdpAuthorableData[0].data.items[0].data.items.filter(
                (data: ContentItem<ContainerData>) =>
                  data.uiComponent !== HARDGOODS_CONSTANTS.PRODUCT_REVIEWS
              );
            this.showSpinner = false;
            this.isProductAvailable = true;
            // if (this.channel.toLowerCase() === CHANNELS.WEB) {
            const data = this.pdpDataHandlerService.loadProductDetailsInPDP(productData);
            this.objectService.set(
              window,
              'digitalData.page.pageInfo.pageName',
              this.objectService.get(data, 'name', '')
            );
            this.pdlWrapperService.trackData('Product-Detail', 'product', {
              product: { productInfo: data },
            });
            this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'pageLoad');
            // }
          }
        },
        (error: APIError) => {
          this.isProductAvailable = false;
          this.showSpinner = false;
        }
      );
  }

  /**
   * updates the selections based on new line or edit line scenario
   * @param ProductDetails ProductDetails
   * @returns void
   */
  private setSelectionDetails(
    productDetails: Product,
    productCanonicalUrl: string,
    itemDetails?: ItemDetail[]
  ): void {
    const selectedColor = this.stateUtilService.getSelectedColor();
    this.productDetails = new ProductDetailsDefaultModel(
      productDetails,
      this.objectService,
      selectedColor,
      this.commonUtilService,
      itemDetails
    );
    this.productType = this.productDetails.getProductDetails().type;
    this.stateUtilService.setSelectedDeviceDetails(this.productDetails, productCanonicalUrl);
    this.productDetailTitle = this.productDetailsService.getProductDetailsTitle(
      this.productDetails,
      this.pdpStaticContent.tabletDetails,
      this.pdpStaticContent.deviceDetails,
      this.pdpStaticContent.accessoriesDetails
    );
  }
  /**
   * Navigate to Landing page
   */
  public navigateHome(): void {
    const route = this.stateUtilService.getCustomerId()
      ? HARDGOODS_CONSTANTS.ACCOUNT_LANDING
      : this.cartsupportService.getHomePage();
    if (route.match(new RegExp(HARDGOODS_CONSTANTS.HTTP_URL, 'i'))) {
      window.location.href = route;
    } else {
      this.router.navigate([route]);
    }
  }
  private setJunkFeeBanner(): void {
    this.isOnlyDevice = this.productType && this.productType !== HARDGOODS_CONSTANTS.ACCESSORYPRODUCTTYPE && !this.productDetails.isEpin();
  }
}
